import { date } from '@e3dc-react/shell/Libs/date'
import moment from '@e3dc-react/shell/Libs/moment'
import { useMediaQuery, useTheme } from '@mui/material'
import React, { useMemo } from 'react'
import {
  Area,
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  Rectangle,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { DateSettings } from 'src/Libs/system-data-handler/types'
import { UnitTypes } from 'src/Libs/types'
import { useTypedSelector } from 'src/State/RootReducer'
import { YAxisDomainConfig } from '../../config'
import { useIsDynamicEmsAccessible } from '../../hooks'
import { useDynamicEmsData } from '../../hooks/dynamic-ems/use-dynamic-ems-data'
import { CostSavingsChartColors, useCostSavingChartColorSettings } from '../../hooks/use-cost-saving-chart-color-settings'
import { CostSavingChartData, CostSavingChartDataWithDynamicEms, DynamicPricesSettings, EmsBatteryMode } from '../../types'
import { checkHasNegativeValuesOnLeftYAxis, formatYTicks } from '../../utils'
import { getCostSavingChartDataWithDynamicEms } from '../../utils/cost-saving-chart'
import { getTicksWithEqualStepSize } from '../../utils/cost-saving-chart/domain'
import { CostSavingChartTooltip } from './tooltip'
import { ChartViews, REFERENCE_AREA_STROKE_OPACITY, useChartDateBarContext, useXAxisTicks } from 'src/Libs/chart-kit'
import { useGradientY2Values } from '../../hooks/dynamic-ems/use-gradient-y2-values'
import { ZoomIndexType } from 'src/Libs/system-charts/shared'
import { CostSavingChartTraceKey, CostSavingChartTraceKeys } from '../../cost-saving-chart-trace-keys'
import { ChartEventHandlers } from 'src/Libs/chart-kit/shared/chart-event-handlers'

interface Props {
  dateSettings: DateSettings
  dynamicPricesSettings: DynamicPricesSettings | null
  data: CostSavingChartData[]
  hiddenTraces: CostSavingChartTraceKey[]
  chartEventHandlers?: ChartEventHandlers
  zoomStartIndex?: ZoomIndexType
  zoomEndIndex?: ZoomIndexType
  isZoomedIn?: boolean
  zoomedFrom?: string | number | null
  zoomedTo?: string | number | null
}

const STACK_ID = '360_savings'

/**
 * Electricity chart which displays the electricity consumption and prices.
 * Combines stacked bars and a line.
 */
const Chart: React.FC<Props> = ({
  dateSettings,
  data,
  hiddenTraces,
  dynamicPricesSettings,
  chartEventHandlers,
  zoomStartIndex,
  zoomEndIndex,
  isZoomedIn,
  zoomedFrom,
  zoomedTo,
}) => {
  const theme = useTheme()
  const downSm = useMediaQuery(theme.breakpoints.down('sm'))
  const upMd = useMediaQuery(theme.breakpoints.up('md'))
  const barSize = useBarSize()
  const dashArray = '3 3'
  const chartColor = useCostSavingChartColorSettings()
  const installationId = useTypedSelector(store => store.storage.installation?.id)?.toString() ?? ''
  const { hasCustomRange } = useChartDateBarContext()

  const { view, from, to, resolution } = dateSettings
  const { isDynamicEmsAccessible, isDynamicEmsV2Accessible } = useIsDynamicEmsAccessible()
  const showDynamicEmsReferenceAreas =
    isDynamicEmsAccessible && !isDynamicEmsV2Accessible && view === 'day' && dynamicPricesSettings?.isDynamicEmsEnabledForBattery

  const usedFrom = useMemo(() => (isZoomedIn ? moment(zoomedFrom) : from), [isZoomedIn, zoomedFrom, from])
  const usedTo = useMemo(() => (isZoomedIn ? moment(zoomedTo) : to), [isZoomedIn, zoomedTo, to])
  const {
    ticks: xAxisTicks,
    format: xAxisTicksFormat,
    interval: xAxisInterval,
  } = useXAxisTicks(usedFrom, usedTo, resolution ?? view, isZoomedIn, hasCustomRange)
  const hasNegativeValuesOnLeftYAxis = checkHasNegativeValuesOnLeftYAxis(data)
  const dataWithoutNullValue = data.filter(d => d[CostSavingChartTraceKeys.ConsumptionPrice] !== null)
  const minValueConsumptionPrice = Math.min(...dataWithoutNullValue.map(d => d[CostSavingChartTraceKeys.ConsumptionPrice] ?? 0))
  const maxValueConsumptionPrice = Math.max(...dataWithoutNullValue.map(d => d[CostSavingChartTraceKeys.ConsumptionPrice] ?? 0))
  const rightYTicks = getTicksWithEqualStepSize(minValueConsumptionPrice, maxValueConsumptionPrice, YAxisDomainConfig.TickCount)
  const rightYDomain = [rightYTicks[0], rightYTicks[rightYTicks.length - 1]]

  const fromString = from.toISOString()
  const toString = date.formatISO(date.addHours(new Date(to.toISOString()), 1))
  const { data: dynamicEmsData, isLoading: isDynamicEmsDataLoading } = useDynamicEmsData(
    installationId,
    fromString,
    toString,
    showDynamicEmsReferenceAreas
  )
  const tickStyleProps = {
    strokeOpacity: downSm ? 0.3 : 1,
    opacity: downSm ? 0.6 : 1,
    fontSize: theme.typography.caption.fontSize,
    lineHeight: theme.typography.caption.lineHeight,
    letterSpacing: theme.typography.caption.letterSpacing,
    fontWeight: theme.typography.caption.fontWeight,
  }

  const dataWithBatteryMode: CostSavingChartData[] | CostSavingChartDataWithDynamicEms[] = useMemo(() => {
    if (!showDynamicEmsReferenceAreas) return data
    return getCostSavingChartDataWithDynamicEms(data, dynamicEmsData)
  }, [showDynamicEmsReferenceAreas, data, dynamicEmsData])

  const { y2BlockDischarging, y2GridCharge, y2NormalOperation } = useGradientY2Values(
    dynamicEmsData,
    dataWithBatteryMode,
    rightYDomain,
    showDynamicEmsReferenceAreas
  )
  const isDayView = view === 'day'
  const tooltipView = showDynamicEmsReferenceAreas && resolution === 'day' ? ChartViews.Hour : resolution ?? view

  return (
    <ResponsiveContainer height={'100%'} width={'100%'} style={{ overflow: 'visible', userSelect: 'none' }}>
      <ComposedChart {...chartEventHandlers} data={dataWithBatteryMode} stackOffset="sign">
        <CartesianGrid strokeDasharray={dashArray} stroke={theme.palette.grey.grey05} opacity={downSm ? 0.3 : 1} vertical={!downSm} />
        {(upMd && zoomStartIndex && zoomEndIndex) || !upMd ? (
          <ReferenceArea yAxisId={'right'} x1={zoomStartIndex || 0} x2={zoomEndIndex || 0} strokeOpacity={REFERENCE_AREA_STROKE_OPACITY} />
        ) : null}
        {hasNegativeValuesOnLeftYAxis && <ReferenceLine y={0} yAxisId={'left'} stroke={theme.palette.grey.grey04} strokeWidth={1} />}
        {showDynamicEmsReferenceAreas && (
          <>
            <defs>
              <linearGradient id={EmsBatteryMode.NormalOperation} x1="0" y1="0" x2="0" y2={y2NormalOperation}>
                <stop offset="5%" stopColor={chartColor.dynamicEmsNormalOperationColor} stopOpacity={1} />
                <stop offset="95%" stopColor={chartColor.dynamicEmsNormalOperationColor} stopOpacity={0} />
              </linearGradient>
              <linearGradient id={EmsBatteryMode.GridCharge} x1="0" y1="0" x2="0" y2={y2GridCharge}>
                <stop offset="5%" stopColor={chartColor.dynamicEmsGridChargeColor} stopOpacity={1} />
                <stop offset="95%" stopColor={chartColor.dynamicEmsGridChargeColor} stopOpacity={0} />
              </linearGradient>
              <linearGradient id={EmsBatteryMode.BlockDischarging} x1="0" y1="0" x2="0" y2={y2BlockDischarging}>
                <stop offset="5%" stopColor={chartColor.dynamicEmsBlockDischargingColor} stopOpacity={1} />
                <stop offset="95%" stopColor={chartColor.dynamicEmsBlockDischargingColor} stopOpacity={0} />
              </linearGradient>
            </defs>
            {isDynamicEmsDataLoading ? (
              <Area
                type="stepAfter"
                dataKey={CostSavingChartTraceKeys.ConsumptionPrice}
                stroke={chartColor.consumptionPriceColor}
                fill={theme.palette.grey.grey06}
                isAnimationActive={false}
                yAxisId={'right'}
                activeDot={false}
              />
            ) : (
              <>
                <Area
                  type="stepAfter"
                  dataKey={EmsBatteryMode.GridCharge}
                  stroke="transparent"
                  fill={`url(#${EmsBatteryMode.GridCharge})`}
                  isAnimationActive={false}
                  yAxisId={'right'}
                  baseValue={'dataMin'}
                  hide={hiddenTraces.includes(EmsBatteryMode.GridCharge) || hiddenTraces.includes(CostSavingChartTraceKeys.ConsumptionPrice)}
                  activeDot={false}
                />
                <Area
                  type="stepAfter"
                  dataKey={EmsBatteryMode.NormalOperation}
                  stroke="transparent"
                  fill={`url(#${EmsBatteryMode.NormalOperation})`}
                  isAnimationActive={false}
                  yAxisId={'right'}
                  baseValue={'dataMin'}
                  hide={hiddenTraces.includes(EmsBatteryMode.NormalOperation) || hiddenTraces.includes(CostSavingChartTraceKeys.ConsumptionPrice)}
                  activeDot={false}
                />
                <Area
                  type="stepAfter"
                  dataKey={EmsBatteryMode.BlockDischarging}
                  stroke="transparent"
                  fill={`url(#${EmsBatteryMode.BlockDischarging})`}
                  isAnimationActive={false}
                  yAxisId={'right'}
                  baseValue={'dataMin'}
                  hide={hiddenTraces.includes(EmsBatteryMode.BlockDischarging) || hiddenTraces.includes(CostSavingChartTraceKeys.ConsumptionPrice)}
                  activeDot={false}
                />
              </>
            )}
          </>
        )}
        <Bar
          isAnimationActive={false}
          dataKey={CostSavingChartTraceKeys.ThreeSixtyBatterySavings}
          barSize={barSize}
          yAxisId={'left'}
          shape={<CustomBarShape chartColor={chartColor} hiddenTraces={hiddenTraces} />}
          stackId={STACK_ID}
          hide={hiddenTraces.includes(CostSavingChartTraceKeys.ThreeSixtyBatterySavings)}
        />
        <Bar
          isAnimationActive={false}
          dataKey={CostSavingChartTraceKeys.ThreeSixtyWallboxSavings}
          barSize={barSize}
          yAxisId={'left'}
          shape={<CustomBarShape chartColor={chartColor} hiddenTraces={hiddenTraces} />}
          stackId={STACK_ID}
          hide={hiddenTraces.includes(CostSavingChartTraceKeys.ThreeSixtyWallboxSavings)}
        />
        {/* This is necessary because there is a bug in the rechart library itself, that breaks the app in case every Bar element is hidden check AF-4155 for more info */}
        <Bar isAnimationActive={false} dataKey={''} yAxisId={'left'} stackId={isDayView ? undefined : STACK_ID} />
        <Line
          isAnimationActive={false}
          type="stepAfter"
          strokeLinejoin="round"
          strokeLinecap="round"
          dataKey={CostSavingChartTraceKeys.ConsumptionPrice}
          stroke={chartColor.consumptionPriceColor}
          yAxisId={'right'}
          dot={false}
          strokeWidth={2}
          hide={hiddenTraces.includes(CostSavingChartTraceKeys.ConsumptionPrice)}
        />
        <YAxis
          yAxisId={'left'}
          width={80}
          orientation="left"
          mirror
          tickFormatter={(value: string, index: number) => formatYTicks(value, UnitTypes.euros)}
          strokeWidth={0}
          tickMargin={downSm ? -20 : -5}
          tick={{ dy: -8, style: { textShadow: '1px 1px 0px white, 1px -1px 0 white,-1px  1px 0 white,-1px -1px 0 white' } }}
          {...tickStyleProps}
        />
        <YAxis
          yAxisId={'right'}
          width={80}
          orientation="right"
          mirror
          tickFormatter={(value: string, index: number) => formatYTicks(value, UnitTypes.cents)}
          strokeWidth={0}
          tickMargin={downSm ? -20 : -5}
          ticks={rightYTicks}
          domain={rightYDomain}
          allowDataOverflow
          tick={{
            dy: -8,
            fill: chartColor.consumptionPriceColor,
            style: { textShadow: '1px 1px 0px white, 1px -1px 0 white,-1px  1px 0 white,-1px -1px 0 white' },
          }}
          {...tickStyleProps}
        />
        <XAxis
          dataKey="time"
          ticks={xAxisTicks}
          tickFormatter={value => moment(value).format(xAxisTicksFormat)}
          scale="point"
          interval={xAxisInterval}
          padding={getXAxisPadding(downSm)}
          {...tickStyleProps}
        />
        <Tooltip
          content={<CostSavingChartTooltip view={tooltipView} hiddenTraces={hiddenTraces} showSummary={view !== ChartViews.Day} />}
          allowEscapeViewBox={downSm ? { y: true } : undefined}
          wrapperStyle={{
            zIndex: theme.zIndex.tooltip,
          }}
        />
      </ComposedChart>
    </ResponsiveContainer>
  )
}

export default Chart

const SQUARE_RADIUS = [0, 0, 0, 0]
const ROUNDED_TOP_RADIUS_SMALL = [2, 2, 0, 0]
const ROUNDED_TOP_RADIUS_MEDIUM = [4, 4, 0, 0]
const ROUNDED_TOP_RADIUS_LARGE = [6, 6, 0, 0]

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const CustomBarShape = (props: any): JSX.Element | null => {
  const theme = useTheme()
  const downSm = useMediaQuery(theme.breakpoints.down('sm'))
  const downMd = useMediaQuery(theme.breakpoints.down('md'))
  const { value, wallboxValue, batteryValue, isWallboxKey, ts, chartColor } = destructureProperties(props)
  const radius = getBarRadius(isWallboxKey, wallboxValue, batteryValue, downSm, downMd)
  const { batteryGradientId, wallboxGradientId, stopOpacityBattery, stopOpacityWallbox, gradientDirectionBattery, gradientDirectionWallbox } =
    getGradientProperties(batteryValue, wallboxValue, ts)

  // Don't render the bar if the value is less than the threshold
  if (value < 0.01 && value > -0.01) {
    return null
  }

  // Otherwise, render the bar as usual
  return (
    <>
      <defs>
        <linearGradient id={batteryGradientId} {...gradientDirectionBattery}>
          <stop offset="0%" stopColor={chartColor.threeSixtySavingsBatteryColor} stopOpacity={1} />
          <stop offset="100%" stopColor={chartColor.threeSixtySavingsBatteryColor} stopOpacity={stopOpacityBattery} />
        </linearGradient>
        <linearGradient id={wallboxGradientId} {...gradientDirectionWallbox}>
          <stop offset="0%" stopColor={chartColor.threeSixtySavingsWallboxColor} stopOpacity={1} />
          <stop offset="100%" stopColor={chartColor.threeSixtySavingsWallboxColor} stopOpacity={stopOpacityWallbox} />
        </linearGradient>
      </defs>
      <Rectangle {...props} radius={radius} fill={`url(#${isWallboxKey ? wallboxGradientId : batteryGradientId})`} />
    </>
  )
}

interface PropertiesReturnType {
  value: number
  ts: number
  wallboxValue: number
  batteryValue: number
  isWallboxKey: boolean
  chartColor: CostSavingsChartColors
}

/**
 * Destructures the properties of chart props.
 *
 * @param props
 * @returns
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const destructureProperties = (props: any): PropertiesReturnType => {
  const { value, dataKey, payload, chartColor, hiddenTraces } = props
  const wallboxKey = CostSavingChartTraceKeys.ThreeSixtyWallboxSavings
  const batteryKey = CostSavingChartTraceKeys.ThreeSixtyBatterySavings
  const wallboxValue = hiddenTraces.includes(wallboxKey) ? 0 : payload[wallboxKey]
  const batteryValue = hiddenTraces.includes(batteryKey) ? 0 : payload[batteryKey]
  const isWallboxKey = dataKey === wallboxKey

  return { value, ts: payload.time, wallboxValue, batteryValue, isWallboxKey, chartColor }
}

/**
 * The bar radius determines weather the bar should have rounded corners or not.
 *
 * @param isWallboxKey
 * @param wallboxValue
 * @param batteryValue
 * @returns
 */
const getBarRadius = (isWallboxKey: boolean, wallboxValue: number, batteryValue: number, downSm: boolean, downMd: boolean): number[] => {
  const invisibleTopBar = wallboxValue === null || wallboxValue === 0
  const haveTopAndBottomBarDifferentDirections = (wallboxValue > 0 && batteryValue < 0) || (wallboxValue < 0 && batteryValue > 0)
  const shouldBeRounded = isWallboxKey || invisibleTopBar || haveTopAndBottomBarDifferentDirections

  if (shouldBeRounded && downSm) return ROUNDED_TOP_RADIUS_SMALL
  if (shouldBeRounded && downMd) return ROUNDED_TOP_RADIUS_MEDIUM
  if (shouldBeRounded) return ROUNDED_TOP_RADIUS_LARGE
  return SQUARE_RADIUS
}
interface GradientPropertiesReturnType {
  batteryGradientId: string
  wallboxGradientId: string
  stopOpacityWallbox: number
  stopOpacityBattery: number
  gradientDirectionBattery: { x1: string; y1: string; x2: string; y2: string }
  gradientDirectionWallbox: { x1: string; y1: string; x2: string; y2: string }
}

/**
 * Provides gradient properties based on the battery and wallbox values.
 *
 * @param batteryValue
 * @param wallboxValue
 * @param ts - used to generate unique gradient ids for each bar
 * @returns
 */
const getGradientProperties = (batteryValue: number, wallboxValue: number, ts: number): GradientPropertiesReturnType => {
  const batteryGradientId = `battery-gradient-${ts}`
  const wallboxGradientId = `wallbox-gradient-${ts}`

  const haveTopAndBottomBarDifferentDirections = (wallboxValue > 0 && batteryValue < 0) || (wallboxValue < 0 && batteryValue > 0)
  const stopOpacityWallbox = batteryValue === 0 || haveTopAndBottomBarDifferentDirections ? 0 : 0.6
  const stopOpacityBattery = 0

  const gradientDirectionBattery = {
    x1: '0',
    y1: batteryValue < 0 ? '1' : '0',
    x2: '0',
    y2: batteryValue < 0 ? '0' : '1',
  }

  const gradientDirectionWallbox = {
    x1: '0',
    y1: wallboxValue < 0 ? '1' : '0',
    x2: '0',
    y2: wallboxValue < 0 ? '0' : '1',
  }

  return { batteryGradientId, wallboxGradientId, stopOpacityWallbox, stopOpacityBattery, gradientDirectionBattery, gradientDirectionWallbox }
}

/**
 * Hook to determine the bar size based on the screen size.
 * @returns
 */
const useBarSize = (): number => {
  const theme = useTheme()
  const downSm = useMediaQuery(theme.breakpoints.down('sm'))
  const downMd = useMediaQuery(theme.breakpoints.down('md'))

  if (downSm) return 8
  if (downMd) return 20
  return 30
}

/**
 * Calculate x axis padding
 *
 * @param downSm
 * @returns
 */
const getXAxisPadding = (downSm: boolean): { left: number; right: number } => {
  if (!downSm) return { left: 20, right: 20 }

  return { left: 0, right: 0 }
}
